// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Open Konvo on", kept on this device. A phone and a desktop want
// different starts (and a browser can be both, by width), so the choice and the
// remembered destination are held per form factor. Read synchronously at boot
// so the root redirect can use it before anything loads from the server.

import {
	isStartChoice,
	isStartDestination,
	type StartAction,
	type StartChoice,
	type StartDestination,
	type StartFormFactor,
} from '@app/features/navigation/utils/StartPageModel';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {makeAutoObservable} from 'mobx';

const STORAGE_KEY = 'konvo:start-page:v1';

type PerFormFactor<T> = Record<StartFormFactor, T>;

interface PersistedStartPage {
	choice: PerFormFactor<StartChoice | null>;
	last: PerFormFactor<StartDestination | null>;
}

function readPersisted(): PersistedStartPage {
	const empty: PersistedStartPage = {choice: {phone: null, desktop: null}, last: {phone: null, desktop: null}};
	try {
		const raw = AppStorage.getItem(STORAGE_KEY);
		if (!raw) return empty;
		const parsed = JSON.parse(raw) as Partial<PersistedStartPage> | null;
		const pick = <T>(value: unknown, guard: (v: unknown) => v is T): T | null => (guard(value) ? value : null);
		return {
			choice: {
				phone: pick(parsed?.choice?.phone, isStartChoice),
				desktop: pick(parsed?.choice?.desktop, isStartChoice),
			},
			last: {
				phone: pick(parsed?.last?.phone, isStartDestination),
				desktop: pick(parsed?.last?.desktop, isStartDestination),
			},
		};
	} catch {
		return empty;
	}
}

class StartPageStore {
	choice: PerFormFactor<StartChoice | null> = {phone: null, desktop: null};
	last: PerFormFactor<StartDestination | null> = {phone: null, desktop: null};
	/** Session only: what the landing page still has to do (open WhatsApp chats, the scratchpad). */
	pendingAction: StartAction | null = null;

	constructor() {
		this.applyPersisted(readPersisted());
		makeAutoObservable(this, {}, {autoBind: true});
		AppStorage.subscribe(() => this.applyPersisted(readPersisted()), {key: STORAGE_KEY, source: 'external'});
	}

	private applyPersisted(next: PersistedStartPage): void {
		this.choice = {...next.choice};
		this.last = {...next.last};
	}

	private persist(): void {
		const snapshot: PersistedStartPage = {choice: {...this.choice}, last: {...this.last}};
		AppStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
	}

	choiceFor(formFactor: StartFormFactor): StartChoice | null {
		return this.choice[formFactor];
	}

	lastFor(formFactor: StartFormFactor): StartDestination | null {
		return this.last[formFactor];
	}

	setChoice(formFactor: StartFormFactor, choice: StartChoice): void {
		this.choice = {...this.choice, [formFactor]: choice};
		this.persist();
	}

	remember(formFactor: StartFormFactor, destination: StartDestination): void {
		if (this.last[formFactor] === destination) return;
		this.last = {...this.last, [formFactor]: destination};
		this.persist();
	}

	setPendingAction(action: StartAction | null): void {
		this.pendingAction = action;
	}

	takePendingAction(): StartAction | null {
		const action = this.pendingAction;
		this.pendingAction = null;
		return action;
	}
}

export default new StartPageStore();
